import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();
    
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');        

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.#tableColorMapping();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const newZoomDialog = newMediaGallery.querySelector('zoom-dialog');
    if (newZoomDialog && !newZoomDialog.querySelector('[ref="media[]"]')) {
      const existingZoomDialog = this.querySelector('zoom-dialog');
      if (existingZoomDialog) {
        newZoomDialog.replaceWith(existingZoomDialog.cloneNode(true));
      } else {
        newZoomDialog.remove();
      }
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  #tableColorMapping() {
    const infoEl = document.querySelector(".other-color-info");
    if (!infoEl) return;
    const skuid = infoEl.getAttribute('data-color_mapping');
    if (!skuid || typeof colorIDsExternal === 'undefined') return;

    const targetNumber = skuid.split("-").slice(-1)[0];
    const match = colorIDsExternal.find((item) => item.number == targetNumber);
    if (!match) return;

    const setText = (selector, text) => {
      const el = document.querySelector(selector);
      if (el) el.innerHTML = text;
    };

    if (match.codes.LDD) setText("td.color-ldd.value", match.codes.LDD.number);
    if (match.codes.LDraw) setText("td.color-ldraw.value", match.codes.LDraw.number + "-" + match.codes.LDraw.title);
    if (match.codes.BrickLink) setText("td.color-bricklink.value", match.codes.BrickLink.number + "-" + match.codes.BrickLink.title);
    if (match.codes.BrickOwl) setText("td.color-brickowl.value", match.codes.BrickOwl.number + "-" + match.codes.BrickOwl.title);
  }

  show2d(event) {
    const btn = event.target;
    const wrapper = btn.closest('.show_type_check');
    wrapper?.querySelector('.type_check_3d')?.classList.remove('cur_show_type');
    btn.classList.add('cur_show_type');
    
    const container = btn.closest('slideshow-slide') ?? btn.closest('.product-media-container');
    container?.querySelector('.product-3d-iframe')?.remove();
  }

  show3d(event) {
    const btn = event.target;
    const wrapper = btn.closest('.show_type_check');
    wrapper?.querySelector('.type_check_2d')?.classList.remove('cur_show_type');
    btn.classList.add('cur_show_type');

    const urlf = btn.getAttribute('data-3d_mid');
    const colorid = btn.getAttribute('data-3d_cid');
    const LDrawId=colorIDsExternal.filter(item=>colorid==item.number);
    const urll = LDrawId[0].codes.LDraw&&LDrawId[0].codes.LDraw.number?LDrawId[0].codes.LDraw.number:"0";
    const iframeSrc =`https://gdd.gobricks.cn/ldrawLast/index3.html?url=https://file.gobricks.cn/products_mpd/${urlf}.mpd&${urll}`; 

    const container = btn.closest('slideshow-slide') ?? btn.closest('.product-media-container');
    if (!container || !iframeSrc) return;
    if (!container.querySelector('.product-3d-iframe')) {
      const iframe = document.createElement('iframe');
      iframe.className = 'product-3d-iframe';
      iframe.src = iframeSrc;
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allow', 'fullscreen; xr-spatial-tracking');
      iframe.setAttribute('allowfullscreen', '');
      container.appendChild(iframe);
    }
  }
  

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
